import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  ListItem,
  ListItemText,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import Icon from '@components/Icon/Icon';
import SkeletonLoader from '@components/SkeletonLoader/SkeletonLoader';

import {
  useGetUserSettings,
  useDisableUser,
  useEnableUser,
} from '@hooks/api/session';
import { useGetUser } from '@hooks/api/user';

import {
  useResendSetupInvitation,
  useResetUserPassword,
} from 'generated/session.generated';
import { useDeleteUser } from 'generated/user.generated';

const messages = defineMessages({
  title: {
    id: 'clientAdminSettings.title',
    defaultMessage: 'Settings',
  },
  'resetUserPassword.title': {
    id: 'clientAdminSettings.resetUserPassword.title',
    defaultMessage: 'Reset User Password',
  },
  'resetUserPassword.subtitle': {
    id: 'clientAdminSettings.resetUserPassword.subtitle',
    defaultMessage: 'Require the client to set a new password upon next login',
  },
  'resendSetupInvitation.title': {
    id: 'clientAdminSettings.resendSetupInvitation.title',
    defaultMessage: 'Send Setup Invitation',
  },
  'resendSetupInvitation.subtitle': {
    id: 'clientAdminSettings.resendSetupInvitation.subtitle',
    defaultMessage:
      'Resend the email to the user that invites them to setup their profile',
  },
  'disableUser.title': {
    id: 'dangerZone.disableUser.title',
    defaultMessage: 'Disable User',
  },
  'disableUser.subtitle': {
    id: 'dangerZone.disableUser.subtitle',
    defaultMessage: 'Disabling a user updates their profile status to Inactive',
  },
  'disableEnableUser.challenge': {
    id: 'dangerZone.disableEnableUser.challenge',
    defaultMessage: 'To {action} this user, enter the text {text}',
  },
  'delete.title': {
    id: 'dangerZone.delete.title',
    defaultMessage: 'Delete User',
  },
  'delete.subtitle': {
    id: 'dangerZone.delete.subtitle',
    defaultMessage:
      'Deleting a user permanently deletes their data and cannot be undone.',
  },
  'enableUser.title': {
    id: 'dangerZone.enableUser.title',
    defaultMessage: 'Enable User',
  },
  'enableUser.subtitle': {
    id: 'dangerZone.enableUser.subtitle',
    defaultMessage: 'Reactivate a user account',
  },
  enable: {
    id: 'dangerZone.enable',
    defaultMessage: 'ENABLE',
  },
  disable: {
    id: 'dangerZone.disable',
    defaultMessage: 'DISABLE',
  },
  delete: {
    id: 'dangerZone.delete',
    defaultMessage: 'DELETE',
  },
});

interface AdminUserSettingsProps {
  /**
   * ID of the user to manage admin settings for
   */
  user_id?: string | null;
}

/**
 * Manage admin functions such as resending invitation emails
 */
function AdminUserSettings({ user_id }: AdminUserSettingsProps) {
  const { formatMessage } = useIntl();
  const { data: userSettingsData, loading: userSettingsLoading } =
    useGetUserSettings({
      user_id: user_id ?? '',
    });
  const { data: userData, loading: userLoading } = useGetUser({
    id: user_id ?? '',
  });

  const [resetUserPassword, { loading: resetUserPasswordLoading }] =
    useResetUserPassword();
  const [disableUser, disableUserLoading] = useDisableUser();
  const [deleteUser, { loading: deleteUserLoading }] = useDeleteUser();
  const [enableUser, enableUserLoading] = useEnableUser();
  const [resendSetupInvitation, { loading: resendSetupInvitationLoading }] =
    useResendSetupInvitation();

  const user = userData?.getUserRecord;
  const isUserDisabled = userSettingsData?.disabled ?? false;
  const loading = userLoading || userSettingsLoading;
  const handleResetUserPassword = async () => {
    if (!user?.email) {
      return;
    }
    await resetUserPassword({
      variables: {
        input: {
          email: user.email,
        },
      },
    });
  };

  const handleDisableEnableUser = async () => {
    if (!user?.email || typeof isUserDisabled !== 'boolean') {
      return;
    }
    if (isUserDisabled) {
      // If user is disabled, re-enable user
      await enableUser({
        input: {
          email: user.email,
        },
      });
    } else {
      // Else disable user
      await disableUser({
        input: {
          email: user.email,
        },
      });
    }
  };

  const handleResendSetupInvitation = async () => {
    const email = user?.email;
    if (!email) {
      return;
    }
    await resendSetupInvitation({
      variables: {
        input: {
          email,
        },
      },
    });
  };

  const handleDeleteUser = async () => {
    if (!user) {
      return;
    }
    await deleteUser({
      variables: {
        input: {
          user_id: user.user_id,
        },
      },
    });
  };

  return (
    <SkeletonLoader loading={loading}>
      <Card>
        <CardHeader title={formatMessage(messages.title)} />
        <Divider />
        <CardContent>
          <ListItem>
            <ListItemText
              primary={formatMessage(messages['resetUserPassword.title'])}
              secondary={formatMessage(messages['resetUserPassword.subtitle'])}
            />
            <Button
              color="primary"
              disabled={Boolean(!user?.email || resetUserPasswordLoading)}
              onClick={handleResetUserPassword}
              startIcon={<Icon name="key" />}
            >
              {formatMessage(messages['resetUserPassword.title'])}
            </Button>
          </ListItem>
          <ListItem>
            <ListItemText
              primary={formatMessage(messages['resendSetupInvitation.title'])}
              secondary={formatMessage(
                messages['resendSetupInvitation.subtitle']
              )}
            />
            <Button
              color="primary"
              disabled={Boolean(!user?.email || resendSetupInvitationLoading)}
              onClick={handleResendSetupInvitation}
              startIcon={<Icon name="paper-plane" type="fad" />}
            >
              {formatMessage(messages['resendSetupInvitation.title'])}
            </Button>
          </ListItem>
          <ListItem>
            <ListItemText
              primary={formatMessage(
                messages[
                  isUserDisabled ? 'enableUser.title' : 'disableUser.title'
                ]
              )}
              secondary={formatMessage(
                messages[
                  isUserDisabled
                    ? 'enableUser.subtitle'
                    : 'disableUser.subtitle'
                ]
              )}
            />
            <ConfirmationDialog
              challenge={{
                phrase: formatMessage(
                  isUserDisabled ? messages.enable : messages.disable
                ),
                description: formatMessage(
                  messages['disableEnableUser.challenge'],
                  {
                    action: isUserDisabled ? 'enable' : 'disable',
                    text: isUserDisabled ? 'ENABLE' : 'DISABLE',
                  }
                ),
                type: 'text',
              }}
              onConfirm={handleDisableEnableUser}
              title={formatMessage(
                messages[
                  isUserDisabled ? 'enableUser.title' : 'disableUser.title'
                ]
              )}
            >
              {({ handleOpen }) => (
                <Button
                  color="error"
                  disabled={
                    !user?.email || disableUserLoading || enableUserLoading
                  }
                  onClick={handleOpen}
                  startIcon={<Icon name="user" />}
                >
                  {formatMessage(
                    messages[
                      isUserDisabled ? 'enableUser.title' : 'disableUser.title'
                    ]
                  )}
                </Button>
              )}
            </ConfirmationDialog>
          </ListItem>
          <ListItem>
            <ListItemText
              primary={formatMessage(messages['delete.title'])}
              secondary={formatMessage(messages['delete.subtitle'])}
            />
            <ConfirmationDialog
              challenge={{
                phrase: formatMessage(messages.delete),
                description: formatMessage(
                  messages['disableEnableUser.challenge'],
                  {
                    action: 'delete',
                    text: 'DELETE',
                  }
                ),
                type: 'text',
              }}
              onConfirm={handleDeleteUser}
              title={formatMessage(messages['delete.title'])}
            >
              {({ handleOpen }) => (
                <Button
                  color="error"
                  disabled={!user?.email || deleteUserLoading}
                  onClick={handleOpen}
                  startIcon={<Icon name="trash" />}
                >
                  {formatMessage(messages['delete.title'])}
                </Button>
              )}
            </ConfirmationDialog>
          </ListItem>
        </CardContent>
      </Card>
    </SkeletonLoader>
  );
}

export default AdminUserSettings;
